import apiClient from './index';

export interface ProviderStatus {
  display_name: string;
  priority: number;
  available: boolean;
  enabled: boolean;
}

export interface IcecatSettings {
  username: string;
  app_key: string;
}

export interface IcecatCacheStats {
  cached_products: number;
  expired_products: number;
}

export interface IcecatSearchResult {
  status: 'success' | 'error';
  found?: boolean;
  product?: unknown;
  message?: string;
  error?: string;
}

export interface IcecatPreseedJob {
  id: number;
  brand: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  products_found: number;
  products_fetched: number;
  products_failed: number;
  error_message?: string;
  started_at?: string;
  completed_at?: string;
  created_at: string;
}

export interface IcecatPreseedResponse {
  status: 'success' | 'error';
  message?: string;
  job_id?: number;
  error?: string;
}

export interface IcecatPreseedJobsResponse {
  status: 'success' | 'error';
  jobs?: IcecatPreseedJob[];
  error?: string;
}

export interface IcecatBackfillJob {
  id: number;
  source_provider: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  products_queued: number;
  products_found: number;
  products_not_found: number;
  products_failed: number;
  error_message?: string;
  started_at?: string;
  completed_at?: string;
  created_at: string;
}

export interface IcecatBackfillResponse {
  status: 'success' | 'error';
  message?: string;
  job_id?: number;
  error?: string;
}

export interface IcecatBackfillJobsResponse {
  status: 'success' | 'error';
  jobs?: IcecatBackfillJob[];
  error?: string;
}

export interface IcecatQueueStats {
  pending: number;
  completed: number;
  found: number;
  not_found: number;
  premium_only: number;
}

export interface EtlSettings {
  client_mode: 'DELTA' | 'FULL';
  client_locale: string;
  cleanup_on_success: boolean;
  connection_retries: number;
  ftp_host: string;
  ftp_username: string;
  ftp_password: string;
  ftp_directory: string;
}

export interface SettingsResponse {
  status: 'success' | 'error';
  message?: string;
  error?: string;
}

export interface TestConnectionResponse {
  status: 'success' | 'error';
  message?: string;
  error?: string;
}

export async function getProviderStatus(): Promise<Record<string, ProviderStatus>> {
  const response = await apiClient.get<Record<string, ProviderStatus>>('/spex/plus/providers/status');
  return response.data;
}

export async function setProviderEnabled(providerName: string, enabled: boolean): Promise<SettingsResponse & { enabled?: boolean }> {
  const response = await apiClient.put<SettingsResponse & { enabled?: boolean }>(
    `/spex/plus/providers/${providerName}/enabled`,
    { enabled }
  );
  return response.data;
}

export async function getIcecatSettings(): Promise<IcecatSettings> {
  const response = await apiClient.get<IcecatSettings>('/spex/plus/providers/icecat');
  return response.data;
}

export async function updateIcecatSettings(settings: Partial<IcecatSettings>): Promise<SettingsResponse> {
  const response = await apiClient.put<SettingsResponse>('/spex/plus/providers/icecat', settings);
  return response.data;
}

export async function testIcecatConnection(credentials?: Partial<IcecatSettings>): Promise<TestConnectionResponse> {
  const response = await apiClient.post<TestConnectionResponse>('/spex/plus/providers/icecat/test', credentials || {});
  return response.data;
}

export async function getIcecatCacheStats(): Promise<IcecatCacheStats> {
  const response = await apiClient.get<IcecatCacheStats>('/spex/plus/providers/icecat/cache');
  return response.data;
}

export async function clearIcecatCache(): Promise<SettingsResponse> {
  const response = await apiClient.delete<SettingsResponse>('/spex/plus/providers/icecat/cache');
  return response.data;
}

export async function refreshProductFromIcecat(productId: string): Promise<SettingsResponse & { product?: unknown }> {
  const response = await apiClient.post<SettingsResponse & { product?: unknown }>(
    `/spex/plus/providers/icecat/refresh/${encodeURIComponent(productId)}`
  );
  return response.data;
}

export async function refreshPimProduct(pimProductId: number): Promise<SettingsResponse & { product?: unknown }> {
  const response = await apiClient.post<SettingsResponse & { product?: unknown }>(
    `/api/v1/products/pim/${pimProductId}/refresh`
  );
  return response.data;
}

export async function deleteProduct(productId: string): Promise<SettingsResponse & { deleted?: string[] }> {
  const response = await apiClient.delete<SettingsResponse & { deleted?: string[] }>(
    `/api/v1/products/${encodeURIComponent(productId)}`
  );
  return response.data;
}

export async function searchIcecatDirect(params: {
  mpn?: string;
  brand?: string;
  gtin?: string;
}): Promise<IcecatSearchResult> {
  const response = await apiClient.post<IcecatSearchResult>(
    '/spex/plus/providers/icecat/search',
    params
  );
  return response.data;
}

export async function startIcecatPreseed(brand: string): Promise<IcecatPreseedResponse> {
  const response = await apiClient.post<IcecatPreseedResponse>(
    '/spex/plus/providers/icecat/preseed',
    { brand }
  );
  return response.data;
}

export async function getIcecatPreseedJobs(): Promise<IcecatPreseedJobsResponse> {
  const response = await apiClient.get<IcecatPreseedJobsResponse>(
    '/spex/plus/providers/icecat/preseed/jobs'
  );
  return response.data;
}

export async function getIcecatPreseedJob(jobId: number): Promise<{ status: string; job?: IcecatPreseedJob; error?: string }> {
  const response = await apiClient.get<{ status: string; job?: IcecatPreseedJob; error?: string }>(
    `/spex/plus/providers/icecat/preseed/jobs/${jobId}`
  );
  return response.data;
}

export async function startIcecatBackfill(sourceProvider: string): Promise<IcecatBackfillResponse> {
  const response = await apiClient.post<IcecatBackfillResponse>(
    '/spex/plus/providers/icecat/backfill',
    { source_provider: sourceProvider }
  );
  return response.data;
}

export async function getIcecatBackfillJobs(): Promise<IcecatBackfillJobsResponse> {
  const response = await apiClient.get<IcecatBackfillJobsResponse>(
    '/spex/plus/providers/icecat/backfill/jobs'
  );
  return response.data;
}

export async function getIcecatQueueStats(): Promise<IcecatQueueStats> {
  const response = await apiClient.get<IcecatQueueStats>(
    '/spex/plus/providers/icecat/queue/stats'
  );
  return response.data;
}

export async function processIcecatQueue(): Promise<SettingsResponse & { found?: number; not_found?: number; failed?: number }> {
  const response = await apiClient.post<SettingsResponse & { found?: number; not_found?: number; failed?: number }>(
    '/spex/plus/providers/icecat/queue/process'
  );
  return response.data;
}

// ============================================================================
// HP Provider
// ============================================================================

export interface HpSettings {
  sftp_host: string;
  sftp_port: number;
  sftp_username: string;
  sftp_password: string;
  sftp_remote_dir: string;
  download_path: string;
  processing_path: string;
  auth_method: 'password' | 'key';
  private_key: string;
  public_key: string;
}

export interface HpSyncJob {
  id: number;
  status: 'pending' | 'running' | 'completed' | 'failed';
  files_found: number;
  files_downloaded: number;
  files_skipped: number;
  files_extracted: number;
  files_failed: number;
  error_message?: string;
  started_at?: string;
  completed_at?: string;
  created_at: string;
}

export async function getHpSettings(): Promise<HpSettings> {
  const response = await apiClient.get<{ status: string; settings: HpSettings }>('/spex/plus/providers/hp');
  return response.data.settings;
}

export async function updateHpSettings(settings: Partial<HpSettings>): Promise<SettingsResponse> {
  const response = await apiClient.put<SettingsResponse>('/spex/plus/providers/hp', settings);
  return response.data;
}

export async function testHpConnection(credentials?: Partial<HpSettings>): Promise<TestConnectionResponse> {
  const response = await apiClient.post<TestConnectionResponse>('/spex/plus/providers/hp/test', credentials || {});
  return response.data;
}

export async function startHpSync(): Promise<{ status: string; message?: string; job_id?: number }> {
  const response = await apiClient.post<{ status: string; message?: string; job_id?: number }>('/spex/plus/providers/hp/sync');
  return response.data;
}

export async function cancelHpSync(): Promise<{ status: string; message?: string }> {
  const response = await apiClient.post<{ status: string; message?: string }>('/spex/plus/providers/hp/sync/cancel');
  return response.data;
}

export async function getHpSyncJobs(): Promise<{ status: string; jobs: HpSyncJob[] }> {
  const response = await apiClient.get<{ status: string; jobs: HpSyncJob[] }>('/spex/plus/providers/hp/sync/jobs');
  return response.data;
}

export async function generateHpSshKey(): Promise<{ status: string; public_key?: string; message?: string }> {
  const response = await apiClient.post<{ status: string; public_key?: string; message?: string }>('/spex/plus/providers/hp/generate-key');
  return response.data;
}

export async function getHpPublicKey(): Promise<{ status: string; public_key?: string; has_private_key?: boolean }> {
  const response = await apiClient.get<{ status: string; public_key?: string; has_private_key?: boolean }>('/spex/plus/providers/hp/public-key');
  return response.data;
}

export async function startHpImport(): Promise<{ status: string; message?: string; job_id?: number }> {
  const response = await apiClient.post<{ status: string; message?: string; job_id?: number }>('/spex/plus/providers/hp/import');
  return response.data;
}

export async function startHpMetadataImport(): Promise<{ status: string; message?: string; job_id?: number }> {
  const response = await apiClient.post<{ status: string; message?: string; job_id?: number }>('/spex/plus/providers/hp/import/metadata');
  return response.data;
}

export interface HpProductStats {
  total_products: number;
  by_category: Array<{ category_name: string; cnt: number }>;
  avg_quality_score: number;
  products_with_images: number;
}

export async function getHpProductStats(): Promise<HpProductStats> {
  const response = await apiClient.get<{ status: string; stats: HpProductStats }>('/spex/plus/providers/hp/products/stats');
  return response.data.stats;
}

export async function reextractHpFiles(): Promise<{ status: string; message?: string; job_id?: number }> {
  const response = await apiClient.post<{ status: string; message?: string; job_id?: number }>('/spex/plus/providers/hp/sync/extract');
  return response.data;
}

// ============================================================================
// ETL / NIQ Brandbank
// ============================================================================

export async function getEtlSettings(): Promise<EtlSettings> {
  const response = await apiClient.get<EtlSettings>('/spex/plus/settings/etl');
  return response.data;
}

export async function updateEtlSettings(settings: Partial<EtlSettings>): Promise<SettingsResponse> {
  const response = await apiClient.put<SettingsResponse>('/spex/plus/settings/etl', settings);
  return response.data;
}
